import * as THREE from "three";

/**
 * Shadow proxies for props: a set of boxes covering the prop's casting surfaces (see withShadowProxy), drawn into the
 * sun's shadow map in place of the prop's own triangles — at no extra draw call: the boxes ride at the end of the
 * prop's biggest casting mesh (a ShadowCarrier), whose colour pass draws only its own range and whose shadow pass
 * draws only the boxes.
 *
 * withShadowProxy(prototype) is called once per prototype (the builders do it); instance() then locks castShadow off
 * on every other mesh of each clone, so whoever sets castShadow later (GameScene.prepareEntity) leaves the carrier as
 * the only caster — and GameScene's own rule still decides whether the carrier casts at all.
 */

/** clips a polygon to z ≥ lo and z ≤ hi (Sutherland–Hodgman, two planes) */
function clipZ(poly: THREE.Vector3[], lo: number, hi: number): THREE.Vector3[] {
  const plane = (pts: THREE.Vector3[], inside: (p: THREE.Vector3) => boolean, at: number): THREE.Vector3[] => {
    const out: THREE.Vector3[] = [];
    for (let i = 0; i < pts.length; i++) {
      const p = pts[i], q = pts[(i + 1) % pts.length];
      const pin = inside(p), qin = inside(q);
      if (pin) out.push(p);
      if (pin !== qin) { const t = (at - p.z) / (q.z - p.z); out.push(new THREE.Vector3().lerpVectors(p, q, t)); }
    }
    return out;
  };
  return plane(plane(poly, (p) => p.z >= lo, lo), (p) => p.z <= hi, hi);
}

/**
 * Adds a "shadowProxy" mesh to `root` built from its shadow-casting meshes (castShadow, opaque) in root space, and
 * marks root.userData.shadowProxy. The prop is cut into slices `step` long across z; in each slice every triangle
 * (clipped to it) marks the cells of an x–y grid (`cell` metres) it covers; the marked cells merge greedily into
 * rectangles (rows into runs, equal runs into blocks), equal neighbouring slices into one: a few boxes that cover
 * every surface — gaps wider than a cell (under a trailer, between a bar's posts) stay open.
 */
export function withShadowProxy<T extends THREE.Object3D>(root: T, step = 0.5, cell = 0.15, merge = 0.04): T {
  root.updateMatrixWorld(true);
  const toRoot = new THREE.Matrix4().copy(root.matrixWorld).invert();
  const tris: THREE.Vector3[][] = [];
  const lo = new THREE.Vector3(Infinity, Infinity, Infinity), hi = new THREE.Vector3(-Infinity, -Infinity, -Infinity);
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh || !mesh.castShadow || mesh.name === "shadowProxy") return;
    const m = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
    if (m.transparent || m.blending !== THREE.NormalBlending) return;
    const g = mesh.geometry, P = g.getAttribute("position"), I = g.getIndex();
    const toR = new THREE.Matrix4().multiplyMatrices(toRoot, mesh.matrixWorld);
    const count = I ? I.count : P.count;
    for (let t = 0; t < count; t += 3) {
      const tri = [0, 1, 2].map((k) => new THREE.Vector3().fromBufferAttribute(P, I ? I.getX(t + k) : t + k).applyMatrix4(toR));
      for (const p of tri) { lo.min(p); hi.max(p); }
      tris.push(tri);
    }
  });
  if (!tris.length) return root;
  const nz = Math.max(1, Math.ceil((hi.z - lo.z) / step));
  const nx = Math.max(1, Math.ceil((hi.x - lo.x) / cell) + 1), ny = Math.max(1, Math.ceil((hi.y - lo.y) / cell) + 1);
  const grids = Array.from({ length: nz }, () => new Uint8Array(nx * ny));
  const ix = (x: number): number => Math.min(nx - 1, Math.max(0, Math.floor((x - lo.x) / cell)));
  const iy = (y: number): number => Math.min(ny - 1, Math.max(0, Math.floor((y - lo.y) / cell)));
  for (const tri of tris) {
    const zl = Math.min(tri[0].z, tri[1].z, tri[2].z), zh = Math.max(tri[0].z, tri[1].z, tri[2].z);
    const s0 = Math.min(nz - 1, Math.floor((zl - lo.z) / step)), s1 = Math.min(nz - 1, Math.floor((zh - lo.z) / step));
    for (let s = s0; s <= s1; s++) {
      const poly = s0 === s1 ? tri : clipZ(tri, lo.z + s * step, lo.z + (s + 1) * step);
      if (poly.length < 2) continue;
      let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity;
      for (const p of poly) { x0 = Math.min(x0, p.x); x1 = Math.max(x1, p.x); y0 = Math.min(y0, p.y); y1 = Math.max(y1, p.y); }
      const grid = grids[s];
      for (let j = iy(y0); j <= iy(y1); j++) for (let i = ix(x0); i <= ix(x1); i++) grid[j * nx + i] = 1;
    }
  }
  // each slice: runs along x per row, equal runs in consecutive rows merged → rectangles [i0, i1, j0, j1]
  const rectsOf = (grid: Uint8Array): number[][] => {
    const open = new Map<string, number[]>();
    const done: number[][] = [];
    for (let j = 0; j < ny; j++) {
      const runs: Array<[number, number]> = [];
      for (let i = 0; i < nx; i++) {
        if (!grid[j * nx + i]) continue;
        let k = i;
        while (k + 1 < nx && grid[j * nx + k + 1]) k++;
        runs.push([i, k]);
        i = k;
      }
      const next = new Map<string, number[]>();
      for (const [a, b] of runs) {
        const key = `${a}:${b}`;
        const r = open.get(key);
        if (r) { r[3] = j; open.delete(key); next.set(key, r); } else next.set(key, [a, b, j, j]);
      }
      for (const r of open.values()) done.push(r);
      open.clear();
      for (const [k, r] of next) open.set(k, r);
    }
    for (const r of open.values()) done.push(r);
    return done.sort((p, q) => p[0] - q[0] || p[2] - q[2] || p[1] - q[1] || p[3] - q[3]);
  };
  // fill each slice's enclosed cells (a closed outline is solid to the sun; open gaps reach the border and stay open)
  const reach = new Uint8Array(nx * ny), queue = new Int32Array(nx * ny);
  for (const grid of grids) {
    reach.fill(0);
    let head = 0, tail = 0;
    const seed = (k: number): void => { if (!grid[k] && !reach[k]) { reach[k] = 1; queue[tail++] = k; } };
    for (let i = 0; i < nx; i++) { seed(i); seed((ny - 1) * nx + i); }
    for (let j = 0; j < ny; j++) { seed(j * nx); seed(j * nx + nx - 1); }
    while (head < tail) {
      const k = queue[head++], i = k % nx, j = (k - i) / nx;
      if (i > 0) seed(k - 1);
      if (i < nx - 1) seed(k + 1);
      if (j > 0) seed(k - nx);
      if (j < ny - 1) seed(k + nx);
    }
    for (let k = 0; k < grid.length; k++) if (!reach[k]) grid[k] = 1;
  }
  // neighbouring slices that differ in at most `merge` of their cells share one block of boxes (their union)
  const boxes: Array<{ r: number[]; z0: number; z1: number }> = [];
  let group: { grid: Uint8Array; z0: number; z1: number } | undefined;
  const flush = (): void => { if (group) for (const r of rectsOf(group.grid)) boxes.push({ r, z0: group.z0, z1: group.z1 }); };
  for (let s = 0; s < nz; s++) {
    const grid = grids[s], z0 = lo.z + s * step, z1 = Math.min(hi.z, z0 + step);
    let filled = 0;
    for (let k = 0; k < grid.length; k++) filled += grid[k];
    if (!filled) { flush(); group = undefined; continue; }
    if (group) {
      let differ = 0, both = 0;
      for (let k = 0; k < grid.length; k++) { differ += grid[k] ^ group.grid[k]; both += grid[k] | group.grid[k]; }
      if (differ <= merge * both) { for (let k = 0; k < grid.length; k++) group.grid[k] |= grid[k]; group.z1 = z1; continue; }
    }
    flush();
    group = { grid: grid.slice(), z0, z1 };
  }
  flush();
  // the boxes, as triangles in root space
  const proxy: number[] = [];
  for (const { r, z0, z1 } of boxes) {
    const x0 = lo.x + r[0] * cell, x1 = Math.min(hi.x, lo.x + (r[1] + 1) * cell), y0 = lo.y + r[2] * cell, y1 = Math.min(hi.y, lo.y + (r[3] + 1) * cell);
    const g = new THREE.BoxGeometry(Math.max(x1 - x0, 0.01), Math.max(y1 - y0, 0.01), Math.max(z1 - z0, 0.01)).toNonIndexed();
    g.translate((x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2);
    proxy.push(...(g.getAttribute("position").array as Float32Array));
    g.dispose();
  }
  // worth it only at under half the casters' triangles (else the prop keeps casting its own)
  if (proxy.length / 9 > tris.length * 0.5) return root;
  // the carrier: the biggest caster; its geometry gets the proxy appended, drawn only in the shadow pass
  let carrier: THREE.Mesh | undefined, most = 0;
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh || !mesh.castShadow) return;
    const m = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
    if (m.transparent || m.blending !== THREE.NormalBlending || Array.isArray(mesh.material)) return;
    const n = mesh.geometry.getAttribute("position").count;
    if (n > most) { most = n; carrier = mesh; }
  });
  if (!carrier) return root;
  const source = carrier.geometry;
  const geometry = source.index ? source.toNonIndexed() : source.clone();
  const real = geometry.getAttribute("position").count, extra = proxy.length / 3;
  const toCarrier = new THREE.Matrix4().multiplyMatrices(toRoot, carrier.matrixWorld).invert();
  const v = new THREE.Vector3();
  for (const name of Object.keys(geometry.attributes)) {
    const attr = geometry.getAttribute(name) as THREE.BufferAttribute;
    const out = new Float32Array((real + extra) * attr.itemSize);
    out.set(attr.array as Float32Array);
    if (name === "position") {
      for (let i = 0; i < extra; i++) {
        v.set(proxy[i * 3], proxy[i * 3 + 1], proxy[i * 3 + 2]).applyMatrix4(toCarrier);
        out[(real + i) * 3] = v.x; out[(real + i) * 3 + 1] = v.y; out[(real + i) * 3 + 2] = v.z;
      }
    }
    geometry.setAttribute(name, new THREE.BufferAttribute(out, attr.itemSize, attr.normalized));
  }
  geometry.userData.mainRange = [0, real];
  geometry.userData.shadowRange = [real, extra];
  geometry.setDrawRange(0, real);
  geometry.computeBoundingSphere();
  geometry.computeBoundingBox();
  const swap = new ShadowCarrier(geometry, carrier.material);
  swap.name = carrier.name;
  swap.position.copy(carrier.position);
  swap.quaternion.copy(carrier.quaternion);
  swap.scale.copy(carrier.scale);
  swap.renderOrder = carrier.renderOrder;
  swap.receiveShadow = carrier.receiveShadow;
  swap.castShadow = true;
  const parent = carrier.parent;
  if (parent) { parent.add(swap); parent.remove(carrier); }
  root.traverse((o) => { if ((o as THREE.Mesh).isMesh && o !== swap) o.castShadow = false; });
  root.userData.shadowProxy = true;
  root.userData.shadowProxyTriangles = extra / 3;
  return root;
}

/**
 * The mesh that carries a prop's shadow proxy: its geometry holds its own triangles and then the proxy's; the colour
 * pass draws the first range (geometry.drawRange), the shadow pass swaps in the proxy's range for its one draw.
 */
export class ShadowCarrier extends THREE.Mesh {
  override onBeforeShadow(): void {
    const r = this.geometry.userData.shadowRange as [number, number] | undefined;
    if (r) this.geometry.setDrawRange(r[0], r[1]);
  }

  override onAfterShadow(): void {
    const r = this.geometry.userData.mainRange as [number, number] | undefined;
    if (r) this.geometry.setDrawRange(r[0], r[1]);
  }
}

/** On a clone of a prototype with a proxy: only the carrier casts, whoever sets castShadow on the other meshes later. */
export function lockShadowCasting(root: THREE.Object3D): void {
  if (!root.userData.shadowProxy) return;
  root.traverse((o) => {
    if (!(o as THREE.Mesh).isMesh || o instanceof ShadowCarrier) return;
    Object.defineProperty(o, "castShadow", { get: () => false, set: () => undefined, configurable: true });
  });
}
